import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import {
  LayoutDashboard, Cpu, PlusCircle, Lightbulb, Zap,
  Wrench, HardHat, Users, ShieldCheck,
  ChevronRight, ChevronDown,
  Settings, LogOut, Moon, Sun, Monitor, Trash2,
} from 'lucide-react';
import { useSellerAuth } from '../context/SellerAuthContext.js';
import { useProducts } from '../context/ProductContext.js';
import { useTheme } from '../context/ThemeContext.js';
import { cn } from './ui/cn.js';

/* ── helpers ── */

function initials(name: string): string {
  return name.split(' ').filter(Boolean).map((w) => w[0]).join('').slice(0, 2).toUpperCase();
}

/* ── nav primitives ── */

function NavItem({ to, label, icon: Icon, end = false }: { to: string; label: string; icon: React.ElementType; end?: boolean }) {
  return (
    <NavLink to={to} end={end}
      className={({ isActive }) => cn(
        'group flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150',
        isActive ? 'bg-blue-600 text-white shadow-md shadow-blue-900/30' : 'text-slate-400 hover:text-white hover:bg-white/8',
      )}
    >
      {({ isActive }) => (
        <>
          <Icon className={cn('w-4 h-4 shrink-0', isActive ? 'text-white' : 'text-slate-500 group-hover:text-slate-300')} />
          <span className="truncate">{label}</span>
        </>
      )}
    </NavLink>
  );
}

function SubNavItem({ to, label, icon: Icon }: { to: string; label: string; icon: React.ElementType }) {
  return (
    <NavLink to={to} end
      className={({ isActive }) => cn(
        'group flex items-center gap-2.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-150',
        isActive ? 'bg-blue-600/90 text-white shadow-sm' : 'text-slate-500 hover:text-slate-200 hover:bg-white/6',
      )}
    >
      {({ isActive }) => (
        <>
          <Icon className={cn('w-3.5 h-3.5 shrink-0', isActive ? 'text-white' : 'text-slate-600 group-hover:text-slate-400')} />
          <span className="truncate">{label}</span>
        </>
      )}
    </NavLink>
  );
}

function CollapsibleSection({ label, icon: Icon, matchPrefix, children }: {
  label: string; icon: React.ElementType; matchPrefix: string; children: React.ReactNode;
}) {
  const { pathname } = useLocation();
  const isActive = pathname.startsWith(matchPrefix);
  const [open, setOpen] = useState(isActive);
  return (
    <div>
      <button type="button" onClick={() => setOpen((o) => !o)}
        className={cn(
          'w-full group flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150',
          isActive ? 'text-white bg-white/10' : 'text-slate-400 hover:text-white hover:bg-white/8',
        )}
      >
        <Icon className={cn('w-4 h-4 shrink-0', isActive ? 'text-blue-400' : 'text-slate-500 group-hover:text-slate-300')} />
        <span className="flex-1 text-left truncate">{label}</span>
        <ChevronDown className={cn('w-3.5 h-3.5 shrink-0 transition-transform duration-200',
          isActive ? 'text-slate-400' : 'text-slate-600 group-hover:text-slate-400',
          open ? 'rotate-180' : '')} />
      </button>
      {open && (
        <div className="mt-1 ml-3 pl-3 border-l border-white/8 space-y-0.5 pb-1">
          {children}
        </div>
      )}
    </div>
  );
}

function NavSection({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-5">
      <p className="px-3 mb-1.5 text-[10px] font-bold tracking-widest text-slate-600 uppercase select-none">{label}</p>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

/* ── profile dropdown ── */

function ProfileMenu() {
  const { seller, logout } = useSellerAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setConfirmDelete(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  async function handleLogout() {
    setOpen(false);
    await logout();
    navigate('/login', { replace: true });
  }

  const themeIcons: Record<string, React.ElementType> = { light: Sun, dark: Moon, system: Monitor };
  const ThemeIcon = themeIcons[theme];

  return (
    <div ref={ref} className="relative">
      {/* Trigger */}
      <button
        type="button"
        onClick={() => { setOpen((o) => !o); setConfirmDelete(false); }}
        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-white/6 transition-all group"
      >
        <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-bold shrink-0 select-none ring-2 ring-blue-500/20">
          {initials(seller?.name ?? 'S')}
        </div>
        <div className="min-w-0 flex-1 text-left">
          <p className="text-sm font-semibold text-slate-200 truncate leading-tight">{seller?.name ?? ''}</p>
          <p className="text-xs text-slate-500 truncate leading-tight mt-0.5">{seller?.mobile ?? ''}</p>
        </div>
        <ChevronDown className={cn('w-3.5 h-3.5 text-slate-600 group-hover:text-slate-400 transition-transform duration-200 shrink-0', open ? 'rotate-180' : '')} />
      </button>

      {/* Dropdown — opens upward */}
      {open && (
        <div className="absolute bottom-full left-0 right-0 mb-2 bg-slate-900 border border-white/10 rounded-xl shadow-2xl shadow-black/50 overflow-hidden z-50">
          {!confirmDelete ? (
            <>
              {/* Header */}
              <div className="px-4 py-3 border-b border-white/8">
                <p className="text-xs font-semibold text-slate-200 truncate">{seller?.name}</p>
                <p className="text-xs text-slate-500 truncate mt-0.5">{seller?.mobile}</p>
              </div>

              {/* Menu items */}
              <div className="py-1.5">
                <button type="button" onClick={() => { setOpen(false); navigate('/settings'); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-white/6 transition-all">
                  <Settings className="w-4 h-4 text-slate-500" />
                  Settings
                </button>

                {/* Theme navigation button */}
                <button type="button"
                  onClick={() => { setOpen(false); navigate('/theme'); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-white/6 transition-all">
                  <ThemeIcon className="w-4 h-4 text-slate-500" />
                  <span className="flex-1 text-left">Theme</span>
                </button>

                <div className="h-px bg-white/8 my-1.5 mx-3" />

                <button type="button" onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-white/6 transition-all">
                  <LogOut className="w-4 h-4 text-slate-500" />
                  Logout
                </button>

                <div className="h-px bg-white/8 my-1.5 mx-3" />

                <button type="button" onClick={() => setConfirmDelete(true)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/8 transition-all">
                  <Trash2 className="w-4 h-4" />
                  Delete Account
                </button>
              </div>
            </>
          ) : (
            /* Delete confirmation */
            <div className="p-4">
              <p className="text-sm font-semibold text-white mb-1">Delete account?</p>
              <p className="text-xs text-slate-400 mb-4 leading-relaxed">
                This will permanently remove your account and all data. This cannot be undone.
              </p>
              <div className="flex gap-2">
                <button type="button" onClick={() => setConfirmDelete(false)}
                  className="flex-1 text-xs font-semibold py-2 rounded-lg bg-white/8 text-slate-300 hover:bg-white/12 transition-all">
                  Cancel
                </button>
                <button type="button"
                  onClick={() => { /* backend call in Phase 2 */ }}
                  className="flex-1 text-xs font-semibold py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-all">
                  Delete
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── breadcrumb ── */

function Breadcrumb() {
  const { pathname } = useLocation();
  const crumbMap: Record<string, string[]> = {
    '/dashboard':                ['Dashboard'],
    '/devices/cop-lop':          ['Devices', 'COP-LOP'],
    '/devices/m8':               ['Devices', 'M8 Light'],
    '/devices/avira':            ['Devices', 'Avira'],
    '/devices/register/cop-lop': ['Devices', 'COP-LOP', 'Register'],
    '/devices/register/m8':      ['Devices', 'M8 Light', 'Register'],
    '/devices/register/avira':   ['Devices', 'Avira', 'Register'],
    '/devices':                  ['Devices'],
    '/services':                 ['Services'],
    '/installers':               ['Installers'],
    '/users':                    ['Users'],
    '/admins':                   ['Admins'],
    '/settings':                 ['Settings'],
    '/theme':                    ['Theme'],
  };
  let crumbs = crumbMap[pathname];
  if (!crumbs && /^\/devices\/[^/]+$/.test(pathname)) crumbs = ['Devices', 'Details'];
  if (!crumbs) return null;
  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-sm">
      {crumbs.map((c, i) => (
        <span key={c} className="flex items-center gap-1.5">
          {i > 0 && <ChevronRight className="w-3.5 h-3.5 text-slate-300" />}
          <span className={i === crumbs!.length - 1 ? 'text-slate-800 font-semibold' : 'text-slate-400'}>{c}</span>
        </span>
      ))}
    </nav>
  );
}

/* ── layout ── */

const PRODUCT_NAV: Record<string, { to: string; label: string; icon: React.ElementType; registerTo: string }> = {
  cop_lop: { to: '/devices/cop-lop', label: 'COP-LOP',  icon: PlusCircle, registerTo: '/devices/register/cop-lop' },
  m8:      { to: '/devices/m8',      label: 'M8 Light', icon: Lightbulb,  registerTo: '/devices/register/m8'      },
  avira:   { to: '/devices/avira',   label: 'Avira',    icon: Zap,        registerTo: '/devices/register/avira'   },
};

export function Layout() {
  const { selected } = useProducts();

  return (
    <div className="flex min-h-screen bg-slate-50">
      {/* ── Sidebar ── */}
      <aside className="flex flex-col shrink-0 bg-slate-950 border-r border-white/5" style={{ width: 'var(--sidebar-w)' }}>
        {/* Logo */}
        <div className="px-5 py-5 border-b border-white/6">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center text-white font-bold text-sm select-none shrink-0 shadow-lg shadow-blue-900/50">H</div>
            <div>
              <span className="block font-bold text-white text-sm leading-none tracking-tight">Haron</span>
              <span className="block text-[10px] font-semibold text-slate-500 leading-none mt-0.5 uppercase tracking-wider">Seller Portal</span>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-5 px-3 overflow-y-auto">
          <div className="mb-5 space-y-0.5">
            <NavItem to="/dashboard" label="Dashboard" icon={LayoutDashboard} end />
          </div>

          {selected.length > 0 && (
            <NavSection label="Devices">
              <CollapsibleSection label="Devices" icon={Cpu} matchPrefix="/devices">
                {selected.map((key) => {
                  const p = PRODUCT_NAV[key];
                  return p ? <SubNavItem key={key} to={p.to} label={p.label} icon={p.icon} /> : null;
                })}
              </CollapsibleSection>
            </NavSection>
          )}

          <NavSection label="People">
            <NavItem to="/installers" label="Installers" icon={HardHat}     end />
            <NavItem to="/users"      label="Users"       icon={Users}       end />
            <NavItem to="/admins"     label="Admins"      icon={ShieldCheck} end />
          </NavSection>

          <NavSection label="Support">
            <NavItem to="/services" label="Services" icon={Wrench} end />
          </NavSection>
        </nav>

        {/* Profile footer */}
        <div className="px-3 py-3 border-t border-white/6">
          <ProfileMenu />
        </div>
      </aside>

      {/* ── Main ── */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-20 h-14 bg-white/95 backdrop-blur-sm border-b border-slate-200/80 px-6 flex items-center shrink-0">
          <Breadcrumb />
        </header>
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
